import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Button,
  Chip,
  AccordionDetails,
  TextField,
  Alert,
  Divider,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  DragIndicator as DragIcon,
} from '@mui/icons-material';
import DraggableQuestionList from './DraggableQuestionList';

const TopicCard = ({ 
  topic, 
  onUpdate, 
  onDelete, 
  onAddQuestion, 
  onUpdateQuestion, 
  onDeleteQuestion,
  onReorderQuestions,
  onUpdateQuestionAttempts,
  dragHandleProps = {}
}) => {
  const [expanded, setExpanded] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({
    title: topic.title,
    difficulty: topic.difficulty,
    notes: topic.notes || '',
  });
  const [error, setError] = useState('');

  const handleEdit = () => {
    setEditMode(true);
    setEditData({
      title: topic.title,
      difficulty: topic.difficulty,
      notes: topic.notes || '',
    });
  };

  const handleSave = async () => {
    try {
      await onUpdate(topic.id, editData);
      setEditMode(false);
      setError('');
    } catch (error) {
      setError(error.message);
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    setError('');
  };

  const handleDelete = async () => {
    if (window.confirm(`Are you sure you want to delete the topic "${topic.title}"? This cannot be undone.`)) {
      try {
        await onDelete(topic.id);
      } catch (error) {
        setError(error.message);
      }
    }
  };

  const handleQuestionReorder = (reorderedQuestions) => {
    if (onReorderQuestions) {
      onReorderQuestions(topic.id, reorderedQuestions);
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'easy':
        return 'success';
      case 'medium':
        return 'warning';
      case 'hard':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Card sx={{ 
      mb: 1.5,
      position: 'relative',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
      '&:hover': {
        transform: 'translateY(-2px)',
      }
    }} onClick={(e) => {
      if (
        e.target.closest('.topic-action-btn') ||
        e.target.closest('button') ||
        e.target.closest('input') ||
        e.target.closest('textarea') ||
        e.target.closest('.markdown-content') ||
        window.getSelection()?.toString()
      ) return;
      setExpanded(!expanded);
    }}>
      <CardContent sx={{ p: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              width: 32,
              height: 32,
              borderRadius: 1,
              bgcolor: 'primary.light',
              color: 'white',
              mr: 1.2,
              flexShrink: 0
            }} {...dragHandleProps}>
              <DragIcon sx={{ fontSize: 16 }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" component="h2" sx={{ 
                fontWeight: 600, 
                mb: 0.2,
                color: 'text.primary',
                lineHeight: 1.2,
                fontSize: '1.05rem'
              }}>
                {topic.title}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Chip 
                  label={topic.difficulty.charAt(0).toUpperCase() + topic.difficulty.slice(1)} 
                  color={getDifficultyColor(topic.difficulty)}
                  size="small"
                  sx={{ 
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    height: 20
                  }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.85rem' }}>
                  {topic.questions?.length || 0} questions
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton 
              size="small" 
              onClick={handleEdit}
              className="topic-action-btn"
              sx={{ 
                bgcolor: 'action.hover',
                '&:hover': { bgcolor: 'action.selected' },
                p: 0.5
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton 
              size="small" 
              onClick={handleDelete} 
              color="error"
              className="topic-action-btn"
              sx={{ 
                bgcolor: 'error.light',
                color: 'white',
                '&:hover': { bgcolor: 'error.main' },
                p: 0.5
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
            <IconButton 
              size="small" 
              onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
              className="topic-action-btn"
              sx={{ 
                bgcolor: 'action.hover',
                '&:hover': { bgcolor: 'action.selected' },
                p: 0.5
              }}
            >
              <ExpandMoreIcon 
                sx={{ 
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s ease',
                  fontSize: 18
                }} 
              />
            </IconButton>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {editMode && (
          <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'background.default', borderRadius: 1 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, fontSize: '1rem' }}>
              Edit Topic
            </Typography>
            <TextField
              fullWidth
              label="Topic Title"
              value={editData.title}
              onChange={(e) => setEditData({ ...editData, title: e.target.value })}
              sx={{ mb: 1 }}
            />
            <TextField
              fullWidth
              label="Difficulty"
              select
              value={editData.difficulty}
              onChange={(e) => setEditData({ ...editData, difficulty: e.target.value })}
              sx={{ mb: 1 }}
              SelectProps={{
                native: true,
              }}
            >
              <option value="unrated">Unrated</option>
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
            </TextField>
            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={2}
              value={editData.notes}
              onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
              sx={{ mb: 1.5 }}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                variant="contained" 
                onClick={handleSave}
                size="small"
                sx={{ px: 2, fontWeight: 600 }}
              >
                Save Changes
              </Button>
              <Button 
                variant="outlined" 
                onClick={handleCancel}
                size="small"
                sx={{ px: 2, fontWeight: 600 }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        )}

        {!editMode && topic.notes && (
          <Box sx={{ 
            mt: 1,
            p: 1,
            bgcolor: 'background.default', 
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            fontSize: '0.9rem'
          }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', lineHeight: 1.4 }}>
              {topic.notes}
            </Typography>
          </Box>
        )}
      </CardContent>

      {expanded && (
        <AccordionDetails sx={{ pt: 0, px: 1.5, pb: 1.5 }}>
          <Divider sx={{ mb: 1.5 }} />
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '1rem' }}>
              Questions ({topic.questions?.length || 0})
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => onAddQuestion(topic)}
              sx={{
                px: 1.5,
                py: 0.5,
                fontSize: '0.8rem',
                fontWeight: 600,
                borderRadius: 1.5,
              }}
            >
              Add Question
            </Button>
          </Box>

          {topic.questions && topic.questions.length > 0 ? (
            <DraggableQuestionList
              questions={topic.questions}
              onUpdate={(questionData) => onUpdateQuestion(topic.id, questionData)}
              onDelete={(questionId) => onDeleteQuestion(topic.id, questionId)}
              onReorder={handleQuestionReorder}
              onAttemptUpdate={(questionId, attempts) => onUpdateQuestionAttempts(topic.id, questionId, attempts)}
            />
          ) : (
            <Box 
              textAlign="center" 
              py={2}
              sx={{
                bgcolor: 'background.default',
                borderRadius: 1.5,
                border: '2px dashed',
                borderColor: 'divider'
              }}
            >
              <Typography variant="body1" color="text.secondary" gutterBottom sx={{ fontWeight: 500, fontSize: '0.95rem' }}>
                No questions yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.85rem' }}>
                Add your first question to start practicing this topic
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => onAddQuestion(topic)}
                sx={{ fontWeight: 600, fontSize: '0.8rem' }}
              >
                Add Question
              </Button>
            </Box>
          )}
        </AccordionDetails>
      )}
    </Card>
  );
};

export default TopicCard; 